import React from 'react';
import './StepProgressIndicator.css';

/**
 * StepProgressIndicator Component
 * 
 * Displays a multi-step progress indicator with numbered circles
 * Shows current step, completed steps, and upcoming steps
 * Allows backward navigation by clicking completed steps
 * 
 * @component
 * @param {Object} props
 * @param {number} props.currentStep - Current active step (1-3)
 * @param {number} props.totalSteps - Total number of steps (default: 3)
 * @param {Function} props.onStepClick - Callback when clicking a step (optional)
 */
const StepProgressIndicator = ({ currentStep = 1, totalSteps = 3, onStepClick }) => {
  /**
   * Handle step click
   * Allow backward navigation only (to completed steps)
   */
  const handleStepClick = (stepNumber) => {
    // Only allow clicking previous steps (backward navigation)
    if (stepNumber < currentStep && onStepClick) {
      onStepClick(stepNumber);
    }
  };
  return (
    <div className="step-progress">
      {/* Render each step circle */}
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isClickable = isCompleted && onStepClick;
        
        return (
          <React.Fragment key={stepNumber}>
            {/* Step Circle */}
            <div 
              className={`step-progress__circle ${
                isCompleted ? 'step-progress__circle--completed' : ''
              } ${
                isActive ? 'step-progress__circle--active' : ''
              } ${
                isClickable ? 'step-progress__circle--clickable' : ''
              }`}
              onClick={() => handleStepClick(stepNumber)}
              style={{ 
                cursor: isClickable ? 'pointer' : 'default'
              }}
            >
              {isCompleted ? (
                // Checkmark for completed steps
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                  <path 
                    d="M1 5L4.5 8.5L11 1.5" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                // Step number for active/upcoming steps
                <span className="step-progress__number">{stepNumber}</span>
              )}
            </div>
            
            {/* Connecting Line (don't show after last step) */}
            {stepNumber < totalSteps && (
              <div 
                className={`step-progress__line ${
                  isCompleted ? 'step-progress__line--completed' : ''
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepProgressIndicator;


